import React, { useEffect, useState, useCallback } from "react";
import { api, fmt } from "@/lib/api";
import { PageHeader, Card } from "@/components/Primitives";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ui } from "@/components/Primitives";
import { toast } from "sonner";
import {
  Users, UserCheck, DollarSign, FileStack, Globe, Mail, MailWarning, Download,
  Activity, Search, ShieldAlert, CreditCard, Database, Loader2, UserPlus, MailCheck, KeyRound,
  FileText, FileSpreadsheet, ChevronLeft, ChevronRight, ScrollText,
} from "lucide-react";

const TIERS = [
  { key: "personal", label: "Personal" },
  { key: "investor", label: "Investor" },
  { key: "community", label: "Community" },
  { key: "all_access", label: "All Access" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

function Stat({ icon: Icon, label, value, sub, tone = "#C6A45C" }) {
  return (
    <Card>
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#8A7F70]">{label}</div>
        <Icon size={18} style={{ color: tone }} strokeWidth={1.5} />
      </div>
      <div className="font-display text-3xl mt-3 font-light text-[#2C2A28] tabular-nums">{value}</div>
      {sub && <div className="text-xs text-[#A99F90] mt-2">{sub}</div>}
    </Card>
  );
}

function HealthPill({ label, status }) {
  const ok = status === "ok" || status === "configured";
  const color = ok ? "#8FB996" : status === "not_configured" ? "#C6A45C" : "#C99FA0";
  const text = status === "configured" ? "Connected" : status === "not_configured" ? "Not configured" : status === "ok" ? "Healthy" : "Down";
  return (
    <div className="flex items-center justify-between py-3 border-b border-[#EAE0D2] last:border-0">
      <span className="text-sm text-[#6b6256]">{label}</span>
      <span className="flex items-center gap-2 text-sm" style={{ color }}>
        <span className="w-2 h-2 rounded-full" style={{ background: color }} /> {text}
      </span>
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [ov, setOv] = useState(null);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [detail, setDetail] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [audit, setAudit] = useState({ records: [], total: 0, page: 1, pages: 1, filters: { admins: [], actions: [] } });
  const [af, setAf] = useState({ admin: "", action: "", target: "", start: "", end: "", q: "" });
  const [auditPage, setAuditPage] = useState(1);

  const loadAudit = useCallback((filters, page) => {
    const params = { ...filters, page, limit: 20 };
    Object.keys(params).forEach((k) => params[k] === "" && delete params[k]);
    api.get("/admin/audit", { params }).then((r) => setAudit(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (tab === "audit") loadAudit(af, auditPage); }, [tab, af, auditPage, loadAudit]);

  const setFilter = (k, v) => { setAf((p) => ({ ...p, [k]: v })); setAuditPage(1); };

  const exportAudit = async (fmt) => {
    try {
      const params = { ...af, fmt };
      Object.keys(params).forEach((k) => params[k] === "" && delete params[k]);
      const res = await api.get("/admin/audit/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `finetomi-audit-log.${fmt === "pdf" ? "pdf" : "xlsx"}`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Audit log exported");
    } catch { toast.error("Could not export audit log"); }
  };

  const loadUsers = useCallback((query = "") => {
    api.get("/admin/users", { params: { q: query } }).then((r) => setUsers(r.data.users)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/admin/overview").then((r) => setOv(r.data)).catch((e) => setErr(e.response?.data?.detail || "Not available"));
    loadUsers();
    api.get("/admin/logs").then((r) => setLogs(r.data.logs)).catch(() => {});
  }, [loadUsers]);

  const changeTier = async (uid, tier) => {
    setSavingId(uid);
    try {
      await api.put(`/admin/users/${uid}/tier`, { tier });
      setUsers((us) => us.map((u) => (u.id === uid ? { ...u, tier } : u)));
      if (detail?.id === uid) setDetail((d) => ({ ...d, tier }));
      toast.success("Plan updated");
    } catch { toast.error("Could not update plan"); }
    finally { setSavingId(null); }
  };

  const openDetail = async (uid) => {
    try { const { data } = await api.get(`/admin/users/${uid}`); setDetail(data); }
    catch { toast.error("Could not load user"); }
  };

  const WS_LIST = [["personal", "Personal Finance"], ["portfolio", "Portfolio"], ["community", "Community"]];
  const toggleUserWorkspace = async (uid, key, on) => {
    if (key === "personal") return;
    const cur = new Set(detail?.workspaces || []);
    if (on) cur.add(key); else cur.delete(key);
    try {
      const { data } = await api.put(`/workspaces/users/${uid}`, { workspaces: Array.from(cur) });
      setDetail((d) => ({ ...d, workspaces: data.enabled }));
      toast.success("Workspaces updated");
    } catch { toast.error("Could not update workspaces"); }
  };

  const [acting, setActing] = useState("");
  const resendVerification = async (uid) => {
    setActing("verify");
    try {
      const { data } = await api.post(`/admin/users/${uid}/resend-verification`);
      if (data.already_verified) toast.info("This user's email is already verified");
      else if (data.sent) toast.success("Verification email sent");
      else toast.warning("Email not delivered — share this link", { description: data.verify_link || "Email service is not configured", duration: 12000 });
    } catch { toast.error("Could not resend verification"); }
    finally { setActing(""); }
  };
  const resetPassword = async (uid) => {
    setActing("reset");
    try {
      const { data } = await api.post(`/admin/users/${uid}/reset-password`);
      if (data.sent) toast.success("Password reset email sent");
      else toast.warning("Email not delivered — share this reset link", { description: data.reset_link || "Email service is not configured", duration: 12000 });
      api.get("/admin/logs").then((r) => setLogs(r.data.logs)).catch(() => {});
    } catch { toast.error("Could not reset password"); }
    finally { setActing(""); }
  };

  if (err) return <div><PageHeader title="Platform Admin" /><Card><div className="text-[#b06b6b] text-sm" data-testid="admin-error">{err}</div></Card></div>;
  if (!ov) return <div className="text-[#8A7F70] animate-pulse">Loading admin portal…</div>;

  const TABS = [["overview", "Overview"], ["users", "Users"], ["audit", "Audit Log"], ["system", "System"]];

  return (
    <div data-testid="admin-portal">
      <PageHeader title="Platform Admin" subtitle="Internal CRM & system health. Billing arrives with Stripe in a later release." />

      <div className="flex gap-2 mb-6">
        {TABS.map(([k, label]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-full text-sm transition-all ${tab === k ? "bg-[#C6A45C]/15 text-[#8a6d2f] border border-[#C6A45C]/30" : "text-[#6b6256] border border-transparent hover:bg-white/70"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-6" data-testid="admin-overview">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <Stat icon={Users} label="Total Users" value={ov.total_users} />
            <Stat icon={UserPlus} label="New (30 days)" value={ov.new_signups_30d} sub={`${ov.new_signups_7d} in last 7 days`} />
            <Stat icon={UserCheck} label="Email Verified" value={ov.verified_count} sub={`${ov.unverified_count} unverified`} tone="#8FB996" />
            <Stat icon={DollarSign} label="Est. MRR" value={fmt(ov.estimated_mrr, "USD")} sub="From active plans" />
            <Stat icon={FileStack} label="Documents" value={ov.documents} />
            <Stat icon={Download} label="Exports" value={ov.exports_count} sub="Reports generated" />
            <Stat icon={Mail} label="Emails Sent" value={ov.emails_sent} tone="#8FB996" />
            <Stat icon={MailWarning} label="Failed Emails" value={ov.failed_emails} tone="#C99FA0" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <Card data-testid="admin-tiers">
              <h3 className="font-display text-xl mb-4">Users by Plan</h3>
              <div className="space-y-3">
                {Object.entries(ov.tier_counts).map(([t, c]) => (
                  <div key={t} className="flex items-center justify-between text-sm">
                    <span className="text-[#6b6256] capitalize">{t.replace("_", " ")}</span>
                    <span className="font-mono-data text-[#2C2A28]">{c}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card>
              <h3 className="font-display text-xl mb-4 flex items-center gap-2"><Globe size={18} className="text-[#C6A45C]" /> Countries</h3>
              <div className="space-y-3">
                {Object.entries(ov.country_counts).map(([c, n]) => (
                  <div key={c} className="flex items-center justify-between text-sm">
                    <span className="text-[#6b6256]">{c}</span><span className="font-mono-data text-[#2C2A28]">{n}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card>
              <h3 className="font-display text-xl mb-4">Currencies</h3>
              <div className="space-y-3">
                {Object.entries(ov.currency_counts).map(([c, n]) => (
                  <div key={c} className="flex items-center justify-between text-sm">
                    <span className="text-[#6b6256]">{c}</span><span className="font-mono-data text-[#2C2A28]">{n}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}

      {tab === "users" && (
        <div data-testid="admin-users">
          <div className="relative mb-4 max-w-md">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A99F90]" />
            <Input data-testid="admin-user-search" value={q} placeholder="Search by name or email…"
              onChange={(e) => { setQ(e.target.value); loadUsers(e.target.value); }} className={`${ui.input} pl-10`} />
          </div>
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-[#8A7F70] border-b border-[#EAE0D2]">
                    <th className="px-5 py-3 font-medium">User</th>
                    <th className="px-3 py-3 font-medium">Plan</th>
                    <th className="px-3 py-3 font-medium">Region</th>
                    <th className="px-3 py-3 font-medium">Verified</th>
                    <th className="px-3 py-3 font-medium">Docs</th>
                    <th className="px-3 py-3 font-medium">Last login</th>
                    <th className="px-3 py-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-b border-[#F0E9DD] hover:bg-white/60 transition-colors">
                      <td className="px-5 py-3">
                        <button onClick={() => openDetail(u.id)} data-testid={`admin-user-open-${u.id}`} className="text-left">
                          <div className="text-[#2C2A28] font-medium hover:text-[#8a6d2f]">{u.name || "—"}{u.role === "admin" && <Badge className="ml-2 bg-[#C6A45C]/15 text-[#8a6d2f] border-0 text-[10px]">admin</Badge>}</div>
                          <div className="text-xs text-[#A99F90]">{u.email}</div>
                        </button>
                      </td>
                      <td className="px-3 py-3">
                        <Select value={u.tier} onValueChange={(v) => changeTier(u.id, v)}>
                          <SelectTrigger data-testid={`admin-tier-select-${u.id}`} className="h-9 w-[130px] bg-white/70 border-[#E4DACB] rounded-lg text-xs">
                            {savingId === u.id ? <Loader2 size={12} className="animate-spin" /> : <SelectValue />}
                          </SelectTrigger>
                          <SelectContent className={ui.selectContent}>
                            {TIERS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-3 text-[#6b6256]">{u.country || "—"} · {u.currency || "—"}</td>
                      <td className="px-3 py-3">{u.email_verified ? <span className="text-[#8FB996]">Yes</span> : <span className="text-[#C99FA0]">No</span>}</td>
                      <td className="px-3 py-3 text-[#6b6256] tabular-nums">{u.documents}</td>
                      <td className="px-3 py-3 text-[#A99F90] text-xs">{fmtDate(u.last_login)}</td>
                      <td className="px-3 py-3 text-right">
                        <button data-testid={`admin-user-view-${u.id}`} onClick={() => openDetail(u.id)}
                          className="text-xs text-[#8a6d2f] hover:underline whitespace-nowrap">View</button>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-[#A99F90]">No users found</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {tab === "audit" && (
        <div data-testid="admin-audit">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.12em] text-[#8A7F70]">Admin</label>
              <Select value={af.admin || "all"} onValueChange={(v) => setFilter("admin", v === "all" ? "" : v)}>
                <SelectTrigger data-testid="audit-filter-admin" className="mt-1 h-10 bg-white/70 border-[#E4DACB] rounded-lg text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className={ui.selectContent}>
                  <SelectItem value="all">All admins</SelectItem>
                  {audit.filters.admins.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.12em] text-[#8A7F70]">Action</label>
              <Select value={af.action || "all"} onValueChange={(v) => setFilter("action", v === "all" ? "" : v)}>
                <SelectTrigger data-testid="audit-filter-action" className="mt-1 h-10 bg-white/70 border-[#E4DACB] rounded-lg text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className={ui.selectContent}>
                  <SelectItem value="all">All actions</SelectItem>
                  {audit.filters.actions.map((a) => <SelectItem key={a} value={a} className="capitalize">{a.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.12em] text-[#8A7F70]">Target user</label>
              <Input data-testid="audit-filter-target" value={af.target} placeholder="email…" onChange={(e) => setFilter("target", e.target.value)} className="mt-1 h-10 bg-white/70 border-[#E4DACB] rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.12em] text-[#8A7F70]">From</label>
              <Input data-testid="audit-filter-start" type="date" value={af.start} onChange={(e) => setFilter("start", e.target.value)} className="mt-1 h-10 bg-white/70 border-[#E4DACB] rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.12em] text-[#8A7F70]">To</label>
              <Input data-testid="audit-filter-end" type="date" value={af.end} onChange={(e) => setFilter("end", e.target.value)} className="mt-1 h-10 bg-white/70 border-[#E4DACB] rounded-lg text-sm" />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A99F90]" />
              <Input data-testid="audit-search" value={af.q} placeholder="Search action, admin, target or detail…"
                onChange={(e) => setFilter("q", e.target.value)} className={`${ui.input} pl-10 mt-0 h-10`} />
            </div>
            <div className="flex gap-2">
              <button data-testid="audit-export-pdf" onClick={() => exportAudit("pdf")}
                className="flex items-center gap-2 text-sm border border-[#D9CDBA] text-[#6b6256] bg-white/60 rounded-xl px-4 py-2.5 hover:border-[#C6A45C] hover:bg-white transition-all">
                <FileText size={15} /> PDF
              </button>
              <button data-testid="audit-export-xlsx" onClick={() => exportAudit("xlsx")}
                className="flex items-center gap-2 text-sm border border-[#D9CDBA] text-[#6b6256] bg-white/60 rounded-xl px-4 py-2.5 hover:border-[#C6A45C] hover:bg-white transition-all">
                <FileSpreadsheet size={15} /> Excel
              </button>
            </div>
          </div>
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-[#8A7F70] border-b border-[#EAE0D2]">
                    <th className="px-5 py-3 font-medium">Timestamp</th>
                    <th className="px-3 py-3 font-medium">Performed by</th>
                    <th className="px-3 py-3 font-medium">Action</th>
                    <th className="px-3 py-3 font-medium">Target</th>
                    <th className="px-3 py-3 font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.records.map((r, i) => (
                    <tr key={i} data-testid="audit-row" className="border-b border-[#F0E9DD] hover:bg-white/60 transition-colors">
                      <td className="px-5 py-3 text-[#6b6256] whitespace-nowrap">{fmtDate(r.at)}</td>
                      <td className="px-3 py-3 text-[#2C2A28]">{r.performed_by_email}</td>
                      <td className="px-3 py-3"><span className="capitalize text-[#8a6d2f] bg-[#C6A45C]/12 rounded-full px-2.5 py-1 text-xs">{(r.action || "").replace(/_/g, " ")}</span></td>
                      <td className="px-3 py-3 text-[#6b6256]">{r.target_email || "—"}</td>
                      <td className="px-3 py-3 text-[#A99F90] text-xs">{r.detail || "—"}</td>
                    </tr>
                  ))}
                  {audit.records.length === 0 && (
                    <tr><td colSpan={5} className="px-5 py-12 text-center text-[#A99F90]"><ScrollText size={22} className="mx-auto mb-2 text-[#C6A45C]" />No audit records match your filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
          <div className="flex items-center justify-between mt-4 text-sm text-[#8A7F70]">
            <span data-testid="audit-total">{audit.total} record{audit.total === 1 ? "" : "s"}</span>
            <div className="flex items-center gap-3">
              <button data-testid="audit-prev" disabled={auditPage <= 1} onClick={() => setAuditPage((p) => Math.max(1, p - 1))}
                className="p-2 rounded-lg border border-[#E4DACB] disabled:opacity-40 hover:bg-white/70"><ChevronLeft size={16} /></button>
              <span>Page {audit.page} of {audit.pages}</span>
              <button data-testid="audit-next" disabled={auditPage >= audit.pages} onClick={() => setAuditPage((p) => p + 1)}
                className="p-2 rounded-lg border border-[#E4DACB] disabled:opacity-40 hover:bg-white/70"><ChevronRight size={16} /></button>
            </div>
          </div>
        </div>
      )}


      {tab === "system" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="admin-system">
          <Card>
            <h3 className="font-display text-xl mb-2 flex items-center gap-2"><Activity size={18} className="text-[#C6A45C]" /> System Health</h3>
            <HealthPill label="Database (MongoDB)" status={ov.health.database} />
            <HealthPill label="Email — Resend" status={ov.health.resend} />
            <HealthPill label="Payments — Stripe" status={ov.health.stripe} />
            <div className="flex items-center gap-2 text-xs text-[#A99F90] mt-4"><CreditCard size={14} /> Stripe billing is a placeholder — enforcement arrives in a later release.</div>
          </Card>
          <Card>
            <h3 className="font-display text-xl mb-4 flex items-center gap-2"><ShieldAlert size={18} className="text-[#C6A45C]" /> Recent Events</h3>
            <div className="space-y-3 max-h-[360px] overflow-y-auto">
              {logs.length === 0 && <div className="text-sm text-[#A99F90] flex items-center gap-2"><Database size={15} /> No recent issues logged.</div>}
              {logs.map((l, i) => (
                <div key={i} className="flex items-start gap-3 text-sm border-b border-[#F0E9DD] pb-3 last:border-0">
                  <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: l.level === "error" ? "#C99FA0" : "#C6A45C" }} />
                  <div>
                    <div className="text-[#6b6256]">{l.message}</div>
                    <div className="text-[11px] text-[#A99F90]">{fmtDate(l.at)} · {l.type}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className={ui.dialog}>
          <DialogHeader><DialogTitle className="font-display text-2xl font-light">User Profile</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4" data-testid="admin-user-detail">
              <div>
                <div className="font-display text-xl text-[#2C2A28]">{detail.name || "—"}</div>
                <div className="text-sm text-[#A99F90]">{detail.email}</div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[["Plan", detail.tier], ["Role", detail.role], ["Country", detail.country || "—"], ["Currency", detail.currency || "—"],
                  ["Household", detail.household || "—"], ["Email verified", detail.email_verified ? "Yes" : "No"],
                  ["Onboarded", detail.onboarding_complete ? "Yes" : "No"], ["Joined", fmtDate(detail.created_at)],
                  ["Last login", fmtDate(detail.last_login)], ["Documents", detail.documents],
                  ["Transactions", detail.transactions], ["Exports", detail.exports_count]].map(([k, v]) => (
                  <div key={k} className="bg-white/60 border border-[#EAE0D2] rounded-xl px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-[#A99F90]">{k}</div>
                    <div className="text-[#2C2A28] capitalize">{String(v)}</div>
                  </div>
                ))}
              </div>
              {detail.profiles?.length > 0 && (
                <div className="text-sm text-[#6b6256]">Profiles: {detail.profiles.join(", ")}</div>
              )}
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A7F70] mb-2">Change plan (beta)</div>
                <Select value={detail.tier} onValueChange={(v) => changeTier(detail.id, v)}>
                  <SelectTrigger data-testid="admin-detail-tier-select" className={ui.selectTrigger}><SelectValue /></SelectTrigger>
                  <SelectContent className={ui.selectContent}>
                    {TIERS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div data-testid="admin-detail-workspaces">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A7F70] mb-2">Workspaces (beta)</div>
                <div className="flex flex-wrap gap-2">
                  {WS_LIST.map(([key, label]) => {
                    const on = (detail.workspaces || []).includes(key);
                    return (
                      <button key={key} data-testid={`admin-ws-${key}`} disabled={key === "personal"}
                        onClick={() => toggleUserWorkspace(detail.id, key, !on)}
                        className={`px-4 py-2 rounded-full text-sm transition-all border ${on ? "bg-[#C6A45C] text-white border-[#C6A45C]" : "bg-white/60 text-[#6b6256] border-[#EAE0D2] hover:border-[#C6A45C]"} ${key === "personal" ? "opacity-70 cursor-default" : ""}`}>
                        {label}{on ? " ✓" : ""}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A7F70] mb-2">Account actions</div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button data-testid="admin-resend-verification" onClick={() => resendVerification(detail.id)} disabled={acting === "verify"}
                    className="flex-1 flex items-center justify-center gap-2 text-sm border border-[#D9CDBA] text-[#6b6256] bg-white/60 rounded-xl px-4 py-2.5 hover:border-[#C6A45C] hover:bg-white transition-all disabled:opacity-60">
                    {acting === "verify" ? <Loader2 size={15} className="animate-spin" /> : <MailCheck size={15} />} Resend verification
                  </button>
                  <button data-testid="admin-reset-password" onClick={() => resetPassword(detail.id)} disabled={acting === "reset"}
                    className="flex-1 flex items-center justify-center gap-2 text-sm border border-[#D9CDBA] text-[#6b6256] bg-white/60 rounded-xl px-4 py-2.5 hover:border-[#C6A45C] hover:bg-white transition-all disabled:opacity-60">
                    {acting === "reset" ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />} Reset password
                  </button>
                </div>
                <p className="text-[11px] text-[#A99F90] mt-2">Actions are recorded in the audit log (System → Recent Events).</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
